import { useCallback, useState } from 'react'
import { Document } from '@contentful/rich-text-types'
import classNames from 'classnames'
import { Button } from '@/atoms/Button'
import CarouselNav from '@/atoms/CarouselNav'
import { PlayCircleIcon } from '@/atoms/Icons/PlayCircleIcon'
import { EyebrowMD, TitleLG, TitleMD } from '@/atoms/Text'
import ContentfulImage from '@/molecules/ContentfulImage'
import { Modal } from '@/molecules/Modal'
import { IMediaGalleryItemFields } from '@/types/generated-contentful'
import useCarousel from '@/utils/useCarousel'
import TextBlock from '../TextBlock'
import MediaGalleryCarousel from './MediaGalleryCarousel'

export interface Props {
  title?: string
  description?: Document
  items: (IMediaGalleryItemFields & { id: string })[]
  className?: string
}

const PlayIcon = ({ size }: { size: 'sm' | 'lg' }) => (
  <span
    className={classNames(
      'absolute top-1/2 left-1/2 translate-x-[-50%] translate-y-[-50%]',
      'flex items-center justify-center',
      'opacity-75 transition-opacity group-hover:opacity-100',
    )}
  >
    <PlayCircleIcon size={size === 'sm' ? 32 : 48} />
  </span>
)

const MediaGallery: React.FC<Props> = ({ title = 'Media Gallery', description, items, className }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [initialItem, setInitialItem] = useState(0)

  const [galleryRef, { canScrollBack, canScrollForward, scrollBack, scrollForward }] = useCarousel<HTMLDivElement>()

  const handleClick = useCallback((idx: number) => {
    setInitialItem(idx)
    setIsOpen(true)
  }, [])

  return (
    <>
      <div className={className}>
        {title && (
          <TitleLG weight="bold" className="mb-4">
            {title}
          </TitleLG>
        )}
        {description && <TextBlock document={description} className="mb-4" />}

        {items[0] && (
          <div className="mb-6 flex w-full flex-col gap-2">
            <Button
              variant="plain"
              onClick={() => handleClick(0)}
              className="group relative overflow-hidden rounded-lg"
            >
              <ContentfulImage
                image={items[0].image}
                className="w-full rounded-lg object-cover"
                containerClassName="aspect-w-16 aspect-h-9"
                fit="fill"
                focus="face"
                lazy
              />
              {!!items[0].video && <PlayIcon size="lg" />}
            </Button>

            {items[0].showTitleOnThumbnail && <TitleMD as={(p) => <h3 {...p} />}>{items[0].title}</TitleMD>}
          </div>
        )}
        <div className="relative">
          <div
            ref={galleryRef}
            className="flex snap-x snap-mandatory gap-4 overflow-x-scroll overscroll-x-contain scrollbar-none"
          >
            {items.slice(1).map((item, idx) => {
              return (
                <Button
                  variant="plain"
                  key={item.id}
                  onClick={() => handleClick(idx + 1)}
                  className="group relative w-3/5 shrink-0 snap-start overflow-hidden rounded-lg md:w-1/3"
                >
                  <ContentfulImage
                    image={item.image}
                    className="rounded-lg object-cover"
                    containerClassName="aspect-h-9 aspect-w-16"
                    fit="fill"
                    focus="face"
                    lazy
                  />
                  {item.showTitleOnThumbnail && (
                    <div className="absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-[rgba(0,0,0,1)] to-[rgba(0,0,0,0)] p-2">
                      <EyebrowMD color="white" className="text-left">
                        {item.title}
                      </EyebrowMD>
                    </div>
                  )}
                  {!!item.video && <PlayIcon size="sm" />}
                </Button>
              )
            })}
          </div>
          <CarouselNav
            onClick={scrollBack}
            active={canScrollBack}
            orientation="horizontal"
            direction="back"
            className="top-0 p-2"
          />

          <CarouselNav
            onClick={scrollForward}
            active={canScrollForward}
            orientation="horizontal"
            direction="forward"
            className="top-0 p-2"
          />
        </div>
      </div>

      <Modal
        isOpen={isOpen}
        backgroundOverlayClassName="!bg-core-gray-950 !bg-opacity-100"
        panelClassesOverride="inline-block w-full overflow-hidden text-left align-middle transition-all transform max-w-none"
      >
        <MediaGalleryCarousel items={items} setIsOpen={setIsOpen} initialItem={initialItem} />
      </Modal>
    </>
  )
}

export default MediaGallery
